import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { RefreshCw, ExternalLink, Trash2, CheckCheck, Radar, Sparkles, AlertCircle, FileSignature } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

function safeHostname(url) {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return "";
  }
}

/** Estrae un nome ente leggibile dall'hostname del bando */
function guessEnte(url) {
  try {
    const host = new URL(url).hostname.replace(/^www\./, "").toLowerCase();
    if (host.includes("invitalia")) return "Invitalia";
    if (host.includes("mise") || host.includes("mimit")) return "Ministero delle Imprese e del Made in Italy (MIMIT)";
    if (host.includes("mef.gov")) return "Ministero dell'Economia e delle Finanze";
    if (host.includes("regione")) {
      const m = host.match(/regione\.([a-z-]+)\./);
      if (m) return `Regione ${m[1].charAt(0).toUpperCase() + m[1].slice(1).replace("-", " ")}`;
    }
    if (host.includes("camcom")) return "Camera di Commercio";
    if (host.includes("europa.eu")) return "Commissione Europea";
    return host.split(".").slice(-2)[0].charAt(0).toUpperCase() + host.split(".").slice(-2)[0].slice(1);
  } catch {
    return "";
  }
}

export default function Bandi() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bandi, setBandi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const prepareDomanda = (b) => {
    const params = new URLSearchParams({
      tab: "domanda",
      titolo: (b.title || "").slice(0, 200),
      ente: guessEnte(b.url),
    });
    navigate(`/app/generatore?${params.toString()}`);
    toast.success("Bando importato — completa importo e motivazione");
  };

  const load = async () => {
    setLoading(true);
    try {
      const [b, c] = await Promise.all([
        api.get("/bandi"),
        api.get("/bandi/unread-count"),
      ]);
      setBandi(b.data);
      setLastRefresh(c.data.last_refresh);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // auto-refresh on first visit if never refreshed
    if (!user) return;
    api.get("/bandi/unread-count").then((r) => {
      if (!r.data.last_refresh && (user.regione || user.settore)) {
        refresh();
      }
    });
    // eslint-disable-next-line
  }, []);

  const [quotaError, setQuotaError] = useState(null);

  const refresh = async () => {
    setRefreshing(true);
    setQuotaError(null);
    try {
      const r = await api.post("/bandi/refresh");
      if (r.data.new_bandi > 0) {
        toast.success(`${r.data.new_bandi} nuovi bandi trovati!`);
      } else {
        toast("Nessun nuovo bando questa volta.", { icon: "🔍" });
      }
      await load();
    } catch (e) {
      // Fix C6: distingui tra errori tecnici e messaggi utente
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        // Errore strutturato dal backend (quota, chiave invalida, ecc.)
        setQuotaError({ code: detail.code, message: detail.message });
        toast.error(detail.message, { duration: 6000 });
      } else {
        toast.error(typeof detail === "string" ? detail : "Errore durante la ricerca");
      }
    } finally {
      setRefreshing(false);
    }
  };

  const openAndRead = async (b) => {
    if (!b.is_read) {
      await api.patch(`/bandi/${b.id}/read`);
      setBandi((prev) => prev.map((x) => x.id === b.id ? { ...x, is_read: true } : x));
    }
    window.open(b.url, "_blank");
  };

  const del = async (id) => {
    await api.delete(`/bandi/${id}`);
    setBandi((prev) => prev.filter((x) => x.id !== id));
  };

  const readAll = async () => {
    await api.post("/bandi/read-all");
    setBandi((prev) => prev.map((x) => ({ ...x, is_read: true })));
    toast.success("Tutti letti");
  };

  const unread = bandi.filter((b) => !b.is_read).length;
  const scored = [...bandi].sort((a, b) => (b.score || 0) - (a.score || 0));

  const hasProfile = user?.regione || user?.settore || user?.codice_ateco;

  return (
    <div className="space-y-6" data-testid="bandi-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-2 flex items-center gap-2">
            <Radar className="w-3.5 h-3.5" /> Radar bandi & finanziamenti
          </div>
          <h1 className="font-heading font-bold text-4xl tracking-tighter">Opportunità per il tuo settore.</h1>
          <p className="text-muted-foreground text-sm mt-2">
            {user?.regione || user?.settore
              ? <>Ricerche mirate per <strong className="text-foreground">{user?.settore || "il tuo settore"}</strong>{user?.regione ? <> in <strong className="text-foreground">{user.regione}</strong></> : null}.</>
              : "Completa il profilo per ricevere bandi filtrati."}
          </p>
        </div>
        <div className="flex gap-2">
          {unread > 0 && (
            <Button variant="outline" onClick={readAll} className="rounded-full" data-testid="bandi-read-all">
              <CheckCheck className="w-4 h-4 mr-2" /> Segna tutti come letti
            </Button>
          )}
          <Button onClick={refresh} disabled={refreshing || !hasProfile} className="rounded-full" data-testid="bandi-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Ricerca in corso..." : "Cerca ora"}
          </Button>
        </div>
      </div>

      {quotaError && (
        <Card className="p-4 md:p-5 rounded-2xl border-2 border-amber-300 bg-amber-50 flex gap-3 items-start" data-testid="bandi-quota-error">
          <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-heading font-semibold text-amber-900 mb-1">
              {quotaError.code === "QUOTA_EXCEEDED" ? "Quota Tavily esaurita" : "Servizio bandi temporaneamente non disponibile"}
            </div>
            <p className="text-amber-800 leading-relaxed">{quotaError.message}</p>
            {quotaError.code === "QUOTA_EXCEEDED" && (
              <p className="text-xs text-amber-700 mt-1">
                Nel frattempo puoi consultare i bandi già trovati sotto. Il sistema riprenderà a cercare automaticamente al rinnovo del piano.
              </p>
            )}
          </div>
        </Card>
      )}

      {!hasProfile && (
        <Card className="p-6 rounded-2xl border-2 border-secondary/20 bg-secondary/5 flex gap-4 items-start" data-testid="bandi-profile-needed">
          <AlertCircle className="w-5 h-5 text-secondary flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-heading font-semibold mb-1">Completa il tuo profilo</div>
            <p className="text-sm text-muted-foreground">
              Per trovare bandi rilevanti abbiamo bisogno almeno di regione, settore o codice ATECO.
              Aggiornalo dalle impostazioni o durante la registrazione.
            </p>
          </div>
        </Card>
      )}

      {lastRefresh && (
        <div className="text-xs text-muted-foreground">
          Ultimo aggiornamento: {new Date(lastRefresh).toLocaleString("it-IT")} · {bandi.length} bandi totali · {unread} non letti
        </div>
      )}

      {bandi.length === 0 && !loading && (
        <Card className="p-12 rounded-2xl border-dashed border-2 text-center" data-testid="bandi-empty">
          <Sparkles className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
          <div className="font-heading font-semibold text-xl mb-1">Nessun bando in archivio</div>
          <div className="text-muted-foreground text-sm mb-4">Clicca "Cerca ora" per lanciare la prima scansione web.</div>
          {hasProfile && (
            <Button onClick={refresh} disabled={refreshing} className="rounded-full" data-testid="bandi-first-search">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Ricerca..." : "Lancia scansione"}
            </Button>
          )}
        </Card>
      )}

      <div className="grid gap-4">
        {scored.map((b) => (
          <Card key={b.id} className={`p-6 rounded-2xl border transition-colors ${b.is_read ? "opacity-70" : "border-primary/20 bg-accent/20"}`} data-testid={`bando-${b.id}`}>
            <div className="flex items-start gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {!b.is_read && <Badge className="bg-secondary text-secondary-foreground rounded-full text-[10px]">NUOVO</Badge>}
                  {b.score > 0.5 && <Badge variant="secondary" className="rounded-full text-[10px]">Rilevanza alta</Badge>}
                  {b.fonti && b.fonti.length > 1 && (
                    <Badge variant="outline" className="rounded-full text-[10px] bg-primary/5 text-primary border-primary/30" data-testid={`bando-fonti-${b.id}`} title={b.fonti.map(f => f.url).join('\n')}>
                      {b.fonti.length} fonti
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString("it-IT")}</span>
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2 leading-tight">{b.title || "Bando senza titolo"}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3 mb-3">{b.snippet}</p>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" onClick={() => openAndRead(b)} className="rounded-full" data-testid={`bando-open-${b.id}`}>
                    Apri bando <ExternalLink className="w-3 h-3 ml-1.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => prepareDomanda(b)} className="rounded-full border-secondary/40 text-secondary hover:bg-secondary/5" data-testid={`bando-prepara-${b.id}`}>
                    <FileSignature className="w-3.5 h-3.5 mr-1.5" /> Prepara domanda
                  </Button>
                  {safeHostname(b.url) && (
                    <span className="text-xs text-muted-foreground truncate max-w-xs">{safeHostname(b.url)}</span>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={() => del(b.id)} data-testid={`bando-del-${b.id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </Card>
        ))}
      </div>

      <div className="text-xs text-muted-foreground pt-8 border-t">
        Ricerca automatica via Tavily. Fonti prioritizzate: Invitalia, MIMIT, Regioni, Camere di Commercio.
        Verifica sempre requisiti e scadenze sul sito ufficiale prima di candidarti.
      </div>
    </div>
  );
}
